import React, { useCallback, useEffect, useState } from 'react';
import { mailApi, fmtSize, invalidateMailStatus, getVacation, setVacation } from '../../data/mailClient';
import { useTenant, useEntityList } from '../../data/entities';

// Mail settings panel. Everyone: signature (kept in this browser) and the
// mailboxes they can open. Admins: manage the domain's mailboxes — create
// (bound to a member, or shared), reassign, delete — against /mail/accounts.
export default function MailSettings({ status, signature, onSignature, onClose, toast, onChanged }) {
  const tenant = useTenant();
  const isAdmin = !!status?.canAdmin;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ localPart: '', displayName: '', uid: '', role: 'personal' });
  const members = useEntityList('member', { org: tenant.orgId }, { enabled: isAdmin && !!tenant.orgId });

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try { setData(await mailApi('/mail/accounts')); setErr(''); } catch (e) { setErr(e.message); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const act = async (body, ok) => {
    setBusy(true); setErr('');
    try {
      await mailApi('/mail/accounts', body);
      toast?.(ok);
      invalidateMailStatus();
      await load();
      onChanged?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Member doc id IS the uid (registry: orgs/{o}/members/{uid}).
  const memberList = members.docs || [];
  const memberName = (uid) => { const m = memberList.find((x) => x.id === uid); return m ? (m.displayName || m.email) : uid; };

  return (
    <div className="ml-settings">
      <div className="ml-settings-head">
        <h2>Mail settings</h2>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Done</button>
      </div>

      <section className="cs-card ml-settings-card">
        <h3>Your mailboxes</h3>
        {status?.mailboxes?.length ? (
          <ul className="ml-plain">
            {status.mailboxes.map((m) => <li key={m.id}><span className="ml-mono">{m.address}</span> <span className="cs-help">{m.role}{m.displayName ? ` · ${m.displayName}` : ''}</span></li>)}
          </ul>
        ) : <p className="cs-help">No mailbox yet — it is created the first time you open Mail.</p>}
        <label className="ml-settings-field">
          <span>Signature</span>
          <textarea className="cs-input" rows={3} value={signature} onChange={(e) => onSignature(e.target.value)} placeholder="Appended to new messages (this browser only)" />
        </label>
        {status?.mailboxes?.[0] && <VacationAndQuota localPart={status.mailboxes[0].id} toast={toast} />}
        <p className="cs-help">Domain <span className="ml-mono">{status?.domain}</span> · {status?.tier || status?.mode} · limits {status?.limits?.perMailboxPerHour}/h · {status?.limits?.perMailboxPerDay}/day per mailbox · {status?.limits?.recipientsPerMessage} recipients · {status?.limits?.attachmentMb} MB attachments{status?.paused ? ' · SENDING PAUSED' : ''}</p>
      </section>

      {isAdmin && (
        <section className="cs-card ml-settings-card">
          <h3>Mailboxes on {status?.domain} <span className="cs-help">admins only</span></h3>
          {err && <p className="ml-err">{err}</p>}
          {!data ? <p className="cs-help">Loading…</p> : (
            <table className="ml-table">
              <thead><tr><th>Address</th><th>Opens for</th><th>Role</th><th>Used</th><th /></tr></thead>
              <tbody>
                {data.mailboxes.map((m) => (
                  <tr key={m.id}>
                    <td className="ml-mono">{m.address}<div className="cs-help">{m.displayName}</div></td>
                    <td>
                      <select className="cs-input ml-select" value={m.uid || ''} disabled={busy} onChange={(e) => act({ action: 'assign', localPart: m.id, uid: e.target.value || null }, `${m.address} reassigned`)}>
                        <option value="">— shared (any member) —</option>
                        {memberList.map((u) => <option key={u.id} value={u.id}>{u.displayName || u.email}</option>)}
                        {m.uid && !memberList.some((u) => u.id === m.uid) && <option value={m.uid}>{memberName(m.uid)}</option>}
                      </select>
                    </td>
                    <td>{m.role}</td>
                    <td className="cs-help">{m.usedBytes != null ? fmtSize(m.usedBytes) : '—'}{m.quotaBytes ? ` / ${fmtSize(m.quotaBytes)}` : ''}</td>
                    <td><button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => { if (window.confirm(`Delete ${m.address} and all its mail?`)) act({ action: 'delete', localPart: m.id }, `${m.address} deleted`); }}>Delete</button></td>
                  </tr>
                ))}
                {!data.mailboxes.length && <tr><td colSpan={5} className="cs-help">No mailboxes yet.</td></tr>}
              </tbody>
            </table>
          )}
          {data?.unmanaged?.length > 0 && (
            <p className="cs-help">On the server but not managed here: {data.unmanaged.map((a) => (
              <button key={a} type="button" className="ml-linkbtn" disabled={busy} onClick={() => act({ action: 'adopt', localPart: String(a).split('@')[0] }, `${a} adopted`)}>{a} (adopt)</button>
            ))}</p>
          )}
          <form className="ml-newbox" onSubmit={(e) => { e.preventDefault(); act({ action: 'create', ...form, uid: form.uid || undefined }, `${form.localPart}@${status?.domain} created`).then(() => setForm({ localPart: '', displayName: '', uid: '', role: 'personal' })); }}>
            <input className="cs-input" placeholder="local part" value={form.localPart} onChange={(e) => setForm({ ...form, localPart: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })} required />
            <span className="ml-mono">@{status?.domain}</span>
            <input className="cs-input" placeholder="display name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            <select className="cs-input ml-select" value={form.uid} onChange={(e) => setForm({ ...form, uid: e.target.value, role: e.target.value ? 'personal' : 'shared' })}>
              <option value="">shared mailbox (any member)</option>
              {memberList.map((u) => <option key={u.id} value={u.id}>{u.displayName || u.email}</option>)}
            </select>
            <button type="submit" className="cs-btn cs-btn-sm" disabled={busy || !form.localPart}>Create</button>
          </form>
          <p className="cs-help">Included mailboxes come with the plan; extra ones bill on the mailboxes meter. Members get a personal mailbox automatically the first time they open Mail.</p>
        </section>
      )}
    </div>
  );
}

// Auto-reply (JMAP VacationResponse through its own backend door) + the
// mailbox storage quota bar (JMAP Quota/get).
function VacationAndQuota({ localPart, toast }) {
  const [data, setData] = useState(null);
  const [v, setV] = useState({ isEnabled: false, subject: '', textBody: '', fromDate: '', toDate: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    getVacation(localPart).then((d) => {
      if (!alive) return;
      setData(d);
      if (d.vacation) setV({ isEnabled: !!d.vacation.isEnabled, subject: d.vacation.subject || '', textBody: d.vacation.textBody || '', fromDate: d.vacation.fromDate ? d.vacation.fromDate.slice(0, 10) : '', toDate: d.vacation.toDate ? d.vacation.toDate.slice(0, 10) : '' });
    }).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [localPart]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await setVacation(localPart, v); toast?.(v.isEnabled ? 'Auto-reply on' : 'Auto-reply off'); } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const q = data?.quota;
  const pct = q?.limit ? Math.min(100, Math.round((q.used / q.limit) * 100)) : null;
  return (
    <>
      {q && (
        <div className="ml-quota" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct ?? 0} aria-label="Mailbox storage">
          <div className="ml-quota-track"><div className={`ml-quota-fill ${pct >= 90 ? 'is-danger' : pct >= 75 ? 'is-warn' : ''}`} style={{ width: `${pct ?? 0}%` }} /></div>
          <span className="cs-help">{fmtSize(q.used || 0)}{q.limit ? ` of ${fmtSize(q.limit)} (${pct}%)` : ' used'}</span>
        </div>
      )}
      <form className="ml-vacation" onSubmit={save}>
        <label className="ml-vac-toggle"><input type="checkbox" checked={v.isEnabled} onChange={(e) => setV({ ...v, isEnabled: e.target.checked })} /> <strong>Auto-reply</strong> <span className="cs-help">(out of office)</span></label>
        {v.isEnabled && (
          <>
            <input className="cs-input" placeholder="Subject" value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} />
            <textarea className="cs-input" rows={3} placeholder="Message" value={v.textBody} onChange={(e) => setV({ ...v, textBody: e.target.value })} />
            <div className="ml-vac-dates">
              <label><span className="cs-help">From</span><input type="date" className="cs-input" value={v.fromDate} onChange={(e) => setV({ ...v, fromDate: e.target.value })} /></label>
              <label><span className="cs-help">Until</span><input type="date" className="cs-input" value={v.toDate} onChange={(e) => setV({ ...v, toDate: e.target.value })} /></label>
            </div>
          </>
        )}
        {err && <p className="ml-err">{err}</p>}
        <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || !data}>{busy ? 'Saving…' : 'Save auto-reply'}</button>
      </form>
    </>
  );
}
